import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity } from 'react-native';
import Svg, { Polyline, Line, Text as SvgText, Circle, Rect } from 'react-native-svg';

import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';

export type Range = '7d' | '30d' | '90d' | 'ytd' | 'all';
export type Granularity = 'day' | 'week' | 'month';

export interface TimeseriesData {
  range: Range;
  granularity: Granularity;
  buckets: string[];
  series: {
    platformFees: number[]; grossSales: number[]; ownerPayouts: number[];
    fleetSales: number[]; nonFleetSales: number[];
    refundsAmount: number[]; avgTxValue: number[];
    washCount: number[]; activeOwners: number[]; newOwners: number[];
    newActivations: number[]; cancelledActivations: number[];
  };
}

type SeriesKey = keyof TimeseriesData['series'];
type Axis = 'left' | 'right';

interface SeriesDef {
  key: SeriesKey;
  label: string;
  color: string;
  axis: Axis;          // left = dollars, right = counts
  defaultOn: boolean;
}

export const SERIES_DEFS: SeriesDef[] = [
  { key: 'platformFees',         label: 'Platform Fees',        color: '#733e24', axis: 'left',  defaultOn: true },
  { key: 'grossSales',           label: 'Gross Sales',          color: '#2563eb', axis: 'left',  defaultOn: false },
  { key: 'ownerPayouts',         label: 'Owner Payouts',        color: '#0891b2', axis: 'left',  defaultOn: false },
  { key: 'fleetSales',           label: 'Fleet Sales',          color: '#7c3aed', axis: 'left',  defaultOn: false },
  { key: 'nonFleetSales',        label: 'Non-Fleet Sales',      color: '#059669', axis: 'left',  defaultOn: false },
  { key: 'refundsAmount',        label: 'Refunds',              color: '#dc2626', axis: 'left',  defaultOn: false },
  { key: 'avgTxValue',           label: 'Avg Tx Value',         color: '#d97706', axis: 'left',  defaultOn: false },
  { key: 'washCount',            label: 'Wash Count',           color: '#0ea5e9', axis: 'right', defaultOn: false },
  { key: 'activeOwners',         label: 'Active Owners',        color: '#16a34a', axis: 'right', defaultOn: false },
  { key: 'newOwners',            label: 'New Owners',           color: '#eab308', axis: 'right', defaultOn: false },
  { key: 'newActivations',       label: 'New Activations',      color: '#10b981', axis: 'right', defaultOn: true },
  { key: 'cancelledActivations', label: 'Cancelled Activations',color: '#ef4444', axis: 'right', defaultOn: false },
];

const RANGES: { value: Range; label: string }[] = [
  { value: '7d', label: '7d' }, { value: '30d', label: '30d' },
  { value: '90d', label: '90d' }, { value: 'ytd', label: 'YTD' },
  { value: 'all', label: 'All' },
];

const GRANULARITIES: { value: Granularity; label: string }[] = [
  { value: 'day', label: 'Day' }, { value: 'week', label: 'Week' }, { value: 'month', label: 'Month' },
];

interface Props {
  data: TimeseriesData | null;
  loading: boolean;
  range: Range;
  onRangeChange: (r: Range) => void;
  granularity: Granularity | null;
  onGranularityChange: (g: Granularity | null) => void;
  selectedKeys: Set<SeriesKey>;
  onToggleSeries: (k: SeriesKey) => void;
}

const fmtMoney = (cents: number) => `$${Math.round(cents / 100).toLocaleString()}`;
const fmtCount = (n: number) => n.toLocaleString();

const formatBucketLabel = (b: string, g: Granularity): string => {
  // b is YYYY-MM-DD
  const d = new Date(b + 'T12:00:00Z');
  if (g === 'month') return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', timeZone: 'UTC' });
};

export function AdminAnalyticsChart({
  data, loading, range, onRangeChange, granularity, onGranularityChange,
  selectedKeys, onToggleSeries,
}: Props) {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [width, setWidth] = useState(800);
  const height = 280;
  const padL = 56, padR = 56, padT = 16, padB = 28;
  const innerW = Math.max(50, width - padL - padR);
  const innerH = height - padT - padB;

  const selected = SERIES_DEFS.filter(s => selectedKeys.has(s.key));

  const { leftMax, rightMax } = useMemo(() => {
    if (!data) return { leftMax: 1, rightMax: 1 };
    let lMax = 0, rMax = 0;
    for (const def of selected) {
      const arr = data.series[def.key];
      const m = Math.max(0, ...arr);
      if (def.axis === 'left') lMax = Math.max(lMax, m);
      else rMax = Math.max(rMax, m);
    }
    return { leftMax: lMax || 1, rightMax: rMax || 1 };
  }, [data, selected]);

  const buckets = data?.buckets ?? [];
  const stepX = buckets.length > 1 ? innerW / (buckets.length - 1) : 0;
  const x = (i: number) => padL + i * stepX;
  const yLeft = (v: number) => padT + innerH - (v / leftMax) * innerH;
  const yRight = (v: number) => padT + innerH - (v / rightMax) * innerH;

  const polyPoints = (arr: number[], axis: Axis) =>
    arr.map((v, i) => `${x(i)},${(axis === 'left' ? yLeft : yRight)(v)}`).join(' ');

  const onMove = (e: any) => {
    if (Platform.OS !== 'web') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const scaleX = width / rect.width;
    const lx = (e.clientX - rect.left) * scaleX;
    const idx = Math.round((lx - padL) / Math.max(1, stepX));
    if (idx >= 0 && idx < buckets.length) setHoverIdx(idx);
    else setHoverIdx(null);
  };

  const leftTicks = [0, 0.25, 0.5, 0.75, 1].map(t => t * leftMax);
  const rightTicks = [0, 0.25, 0.5, 0.75, 1].map(t => t * rightMax);
  const hasLeftSeries = selected.some(s => s.axis === 'left');
  const hasRightSeries = selected.some(s => s.axis === 'right');

  const xLabels = buckets.length > 0
    ? Array.from({ length: Math.min(8, buckets.length) }, (_, i) =>
        Math.round((i / Math.max(1, Math.min(8, buckets.length) - 1)) * (buckets.length - 1)))
    : [];

  return (
    <View style={styles.wrap} onLayout={(e) => setWidth(Math.max(320, e.nativeEvent.layout.width))}>
      {/* Top toolbar: range + granularity */}
      <View style={styles.toolbar}>
        <View style={styles.chipGroup}>
          {RANGES.map(r => (
            <TouchableOpacity key={r.value} onPress={() => onRangeChange(r.value)}
              style={[styles.chip, range === r.value && styles.chipActive]}>
              <Text style={[styles.chipText, range === r.value && styles.chipTextActive]}>{r.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <View style={styles.chipGroup}>
          <TouchableOpacity onPress={() => onGranularityChange(null)}
            style={[styles.chip, granularity === null && styles.chipActive]}>
            <Text style={[styles.chipText, granularity === null && styles.chipTextActive]}>Auto</Text>
          </TouchableOpacity>
          {GRANULARITIES.map(g => (
            <TouchableOpacity key={g.value} onPress={() => onGranularityChange(g.value)}
              style={[styles.chip, granularity === g.value && styles.chipActive]}>
              <Text style={[styles.chipText, granularity === g.value && styles.chipTextActive]}>{g.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Series toggles — wraps to multiple rows on narrow screens */}
      <View style={styles.seriesRow}>
        {SERIES_DEFS.map(s => {
          const on = selectedKeys.has(s.key);
          return (
            <TouchableOpacity key={s.key} onPress={() => onToggleSeries(s.key)}
              style={[styles.seriesChip, on && { backgroundColor: s.color + '15', borderColor: s.color }]}>
              <View style={[styles.seriesDot, { backgroundColor: on ? s.color : colors.border }]} />
              <Text style={[styles.seriesText, on && { color: s.color, fontWeight: fontWeight.semibold }]}>{s.label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Chart */}
      <View style={{ height, marginTop: spacing.sm }}>
        {loading || !data ? (
          <View style={styles.empty}><Text style={styles.emptyText}>{loading ? 'Loading…' : 'No data'}</Text></View>
        ) : selected.length === 0 ? (
          <View style={styles.empty}><Text style={styles.emptyText}>Select at least one series</Text></View>
        ) : (
          <Svg
            width="100%" height={height}
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="none"
            {...(Platform.OS === 'web' ? { onMouseMove: onMove, onMouseLeave: () => setHoverIdx(null) } as any : {})}
          >
            {/* horizontal gridlines */}
            {leftTicks.map((t, i) => (
              <Line key={`g${i}`} x1={padL} y1={yLeft(t)} x2={width - padR} y2={yLeft(t)}
                stroke={colors.borderLight} strokeWidth={0.5} opacity={0.7} />
            ))}
            {/* left axis labels (dollars) */}
            {hasLeftSeries && leftTicks.map((t, i) => (
              <SvgText key={`la${i}`} x={padL - 4} y={yLeft(t) + 3} fontSize={9}
                fill={colors.textSecondary} textAnchor="end">{fmtMoney(t)}</SvgText>
            ))}
            {/* right axis labels (counts) */}
            {hasRightSeries && rightTicks.map((t, i) => (
              <SvgText key={`ra${i}`} x={width - padR + 4} y={yRight(t) + 3} fontSize={9}
                fill={colors.textSecondary} textAnchor="start">{fmtCount(Math.round(t))}</SvgText>
            ))}
            {/* x-axis labels */}
            {xLabels.map((bi, k) => (
              <SvgText key={`x${k}`} x={x(bi)} y={height - 8} fontSize={9}
                fill={colors.textSecondary} textAnchor="middle">
                {formatBucketLabel(buckets[bi], data.granularity)}
              </SvgText>
            ))}
            {/* series polylines */}
            {selected.map(s => (
              <Polyline key={s.key}
                points={polyPoints(data.series[s.key], s.axis)}
                fill="none" stroke={s.color} strokeWidth={2} opacity={0.9} />
            ))}
            {/* hover crosshair + dots + tooltip */}
            {hoverIdx !== null && buckets[hoverIdx] !== undefined && (
              <>
                <Line x1={x(hoverIdx)} y1={padT} x2={x(hoverIdx)} y2={padT + innerH}
                  stroke={colors.textSecondary} strokeWidth={0.5} opacity={0.5} />
                {selected.map(s => {
                  const v = data.series[s.key][hoverIdx];
                  const cy = (s.axis === 'left' ? yLeft : yRight)(v);
                  return <Circle key={s.key} cx={x(hoverIdx)} cy={cy} r={3} fill={s.color} />;
                })}
                <TooltipBox
                  bucketLabel={formatBucketLabel(buckets[hoverIdx], data.granularity)}
                  entries={selected.map(s => ({
                    color: s.color, label: s.label, axis: s.axis,
                    value: data.series[s.key][hoverIdx],
                  }))}
                  anchorX={x(hoverIdx)} anchorY={padT}
                  chartWidth={width}
                  textColor={colors.text} subColor={colors.textSecondary} bg={colors.surface} border={colors.border}
                />
              </>
            )}
          </Svg>
        )}
      </View>
    </View>
  );
}

function TooltipBox({ bucketLabel, entries, anchorX, anchorY, chartWidth, textColor, subColor, bg, border }: {
  bucketLabel: string;
  entries: Array<{ color: string; label: string; value: number; axis: Axis }>;
  anchorX: number; anchorY: number; chartWidth: number;
  textColor: string; subColor: string; bg: string; border: string;
}) {
  const tipW = 170;
  const tipH = 22 + entries.length * 14;
  let tx = anchorX + 10;
  if (tx + tipW > chartWidth - 4) tx = anchorX - tipW - 10;
  const ty = anchorY;

  return (
    <>
      <Rect x={tx} y={ty} width={tipW} height={tipH} rx={6} fill={bg} stroke={border} strokeWidth={0.5} opacity={0.97} />
      <SvgText x={tx + 8} y={ty + 14} fontSize={10} fill={subColor} fontWeight="600">{bucketLabel}</SvgText>
      {entries.map((e, i) => {
        const y = ty + 28 + i * 14;
        const v = e.axis === 'left' ? fmtMoney(e.value) : fmtCount(e.value);
        return (
          <React.Fragment key={i}>
            <Rect x={tx + 8} y={y - 7} width={6} height={6} rx={1.5} fill={e.color} />
            <SvgText x={tx + 18} y={y} fontSize={10} fill={textColor}>{e.label}</SvgText>
            <SvgText x={tx + tipW - 8} y={y} fontSize={10} fill={textColor} textAnchor="end" fontWeight="600">{v}</SvgText>
          </React.Fragment>
        );
      })}
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  wrap: { backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.md, marginBottom: spacing.md,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1 },
  toolbar: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.sm },
  chipGroup: { flexDirection: 'row', gap: 4, flexWrap: 'wrap' },
  chip: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 14, backgroundColor: c.background, borderWidth: 1, borderColor: c.border },
  chipActive: { backgroundColor: c.primary, borderColor: c.primary },
  chipText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.medium },
  chipTextActive: { color: c.white },
  seriesRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, paddingBottom: 4 },
  seriesChip: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, paddingVertical: 5,
    borderRadius: 14, backgroundColor: c.background, borderWidth: 1, borderColor: c.border },
  seriesDot: { width: 8, height: 8, borderRadius: 4 },
  seriesText: { fontSize: fontSize.xs, color: c.textSecondary },
  empty: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  emptyText: { color: c.textSecondary, fontSize: fontSize.sm },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
